import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GetRadarDirectionCommand,
  getIvtClient,
  type RadarDirectionDetail,
} from '../../client/IvtHubClient';
import { type DenialKind, denialKindOf } from '../../utils/authorization';

export interface RadarDirectionState {
  data: RadarDirectionDetail | null;
  loading: boolean;
  error: string | null;
  denial: DenialKind | null;
  reload: () => void;
}

/**
 * GetRadarDirection for the detail modal (DAT-9115, R18; Figma E).
 *
 * `directionId` null = modal is closed. The hook stays mounted (the modal
 * sits beside the card grid so re-opening a different direction is a prop
 * change, not an unmount), so a null id must not fire a request and must
 * leave `loading` false — the modal gates its own render on the id instead.
 *
 * Shape mirrors {@link useRadarDirections} / useAssetFlow: a transient
 * failure keeps the previous response on screen; a 403 drops it. The modal
 * is a reader-only surface (the FP / adopt flow is R19), so a 403 here is
 * surfaced through the modal's own denial panel without a retry button.
 */
export function useRadarDirection(directionId: string | null): RadarDirectionState {
  const [data, setData] = useState<RadarDirectionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [denial, setDenial] = useState<DenialKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Track the directionId the previous effect run saw so we can
  // distinguish an id CHANGE (clear the cached payload) from a RELOAD on
  // the same id (preserve it as the error-Alert backdrop). Compared by
  // identity against the parameter, so a stay-at-null + a flip-to-null
  // both correctly report "unchanged".
  const previousIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const previousId = previousIdRef.current;
    previousIdRef.current = directionId;
    if (directionId === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // On an id CHANGE, blank the previous direction's payload and any
    // stale denial — the header reads `data?.name` and would flash the
    // wrong direction's name while the new fetch is inflight, and a
    // stale 403 Alert would stay visible on top of the new request.
    // On a RELOAD (same id), the keep-last-response-through-failure rule
    // from useRadarDirections applies: the error Alert covers the retry,
    // and the previous payload stays as its backdrop. First effect run
    // (previousId === undefined) counts as an id change — nothing to
    // keep anyway.
    if (previousId !== directionId) {
      setData(null);
      setDenial(null);
    }
    (async () => {
      try {
        const out = await getIvtClient().send(new GetRadarDirectionCommand({ directionId }));
        if (!cancelled) {
          setData(out.direction ?? null);
          setDenial(null);
        }
      } catch (e) {
        if (!cancelled) {
          const kind = denialKindOf(e);
          setDenial(kind);
          if (kind !== null) setData(null);
          setError((e as Error).message ?? 'Unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [directionId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, denial, reload };
}
